import {
  CalendarDate,
  ICheckboxGroupConfig,
  ICheckboxGroupValue,
  IOption,
  IOptionGroup,
} from "component-library";

import { IFacetGroup } from "shared/models/facet";

import { ISortConfig, JobItemReadStatus, SortOrder } from "models/api";

export const DEFAULT_PAGE_NUMBER = 1;

export enum FilterType {
  DateSent = "dateSent",
  ProductionType = "productionType",
  ContractType = "contractType",
  TextFilter = "textFilter",
  PageNumber = "pageNumber",
  ShowType = "showType",
  SortingType = "sortingType",
  Sender = "sender",
  PlayingAgeMin = "minPlayingAge",
  PlayingAgeMax = "maxPlayingAge",
  Locations = "locationId",
}

export interface IJobSender {
  id: string | undefined;
  name: string;
}

export enum SortingType {
  SenderNameAsc = "senderNameAscending",
  SenderNameDesc = "senderNameDescending",
  DateSentAsc = "dateSentOldest",
  DateSentDesc = "dateSentNewest",
  ClosesAsc = "closesAscending",
  ClosesDesc = "closesDescending",
}

export const sortingOptionsToSortFilterMapping: Record<
  SortingType,
  ISortConfig
> = {
  [SortingType.SenderNameAsc]: {
    name: "sender",
    order: SortOrder.Asc,
  },
  [SortingType.SenderNameDesc]: {
    name: "sender",
    order: SortOrder.Desc,
  },
  [SortingType.DateSentAsc]: {
    name: "sent",
    order: SortOrder.Asc,
  },
  [SortingType.DateSentDesc]: {
    name: "sent",
    order: SortOrder.Desc,
  },
  [SortingType.ClosesAsc]: {
    name: "closingDate",
    order: SortOrder.Asc,
  },
  [SortingType.ClosesDesc]: {
    name: "closingDate",
    order: SortOrder.Desc,
  },
};

export interface IFilterModel {
  [FilterType.ProductionType]: ICheckboxGroupValue;
  [FilterType.ContractType]: ICheckboxGroupValue;
  [FilterType.DateSent]: CalendarDate | undefined;
  [FilterType.TextFilter]: string | undefined;
  [FilterType.PageNumber]: number;
  [FilterType.ShowType]: ShowType;
  [FilterType.SortingType]: SortingType;
  [FilterType.Sender]: IJobSender | undefined;
  [FilterType.PlayingAgeMin]?: number;
  [FilterType.PlayingAgeMax]?: number;
  [FilterType.Locations]: IOption<string>[];
}

export enum ShowType {
  AllBreakdowns = "allBreakdowns",
  UnreadBreakdowns = "unreadBreakdowns",
  ReadBreakdowns = "readBreakdowns",
  BreakdownsWithSuggestions = "breakdownsWithSuggestions",
  AllRoles = "allRoles",
  UnreadRoles = "unreadRoles",
  ReadRoles = "readRoles",
  RolesWithSuggestions = "rolesWithSuggestions",
}

export const showTypeToJobItemReadStatusMapping: Partial<
  Record<ShowType, JobItemReadStatus>
> = {
  [ShowType.UnreadBreakdowns]: JobItemReadStatus.Unread,
  [ShowType.ReadBreakdowns]: JobItemReadStatus.Read,
  [ShowType.UnreadRoles]: JobItemReadStatus.Unread,
  [ShowType.ReadRoles]: JobItemReadStatus.Read,
};

export const showTypeToJobItemIsSuggestedMapping: Partial<
  Record<ShowType, boolean>
> = {
  [ShowType.BreakdownsWithSuggestions]: true,
  [ShowType.RolesWithSuggestions]: true,
};

export enum FilterControlType {
  Dropdown = "dropdown",
  DatePicker = "datePicker",
  CheckboxGroup = "checkboxGroup",
  Locations = "Locations",
}

export interface IFilterConfigItem {
  translations: string | Record<string, string>;
  defaultValue: any;
  filterControlType?: FilterControlType;
  drawerFilterControlType?: FilterControlType;
  facets?: IFacetGroup[];
  dropdownOptions?: IOptionGroup<number>[];
  checkboxOptions?: ICheckboxGroupConfig["options"];
}

export type IFilterConfig = Record<
  keyof Pick<
    IFilterModel,
    | FilterType.ProductionType
    | FilterType.ContractType
    | FilterType.DateSent
    | FilterType.Sender
    | FilterType.PlayingAgeMin
    | FilterType.PlayingAgeMax
    | FilterType.Locations
  >,
  IFilterConfigItem
>;

export const DEFAULT_VALUES = {
  [FilterType.DateSent]: undefined,
  [FilterType.ProductionType]: {},
  [FilterType.ContractType]: {},
  [FilterType.TextFilter]: "",
  [FilterType.PageNumber]: DEFAULT_PAGE_NUMBER,
  [FilterType.ShowType]: ShowType.AllBreakdowns,
  [FilterType.SortingType]: SortingType.DateSentDesc,
  [FilterType.Sender]: undefined,
  [FilterType.Locations]: [],
};
